import React, { useEffect, useState } from "react";
import AdminDashboard from "../../components/AdminDashboard";
import { createOrder } from "../../service/OrderService";
import { OrderType, OrderStatus } from "../../my-enum";
import { useNavigate } from "react-router-dom";
import { Form, Button, Panel, Message, SelectPicker, DatePicker, Input, InputNumber, Loader, Checkbox, CheckPicker } from "rsuite";
import { getAllTeamVehicles, TeamVehicle } from '../../service/VehicleService';
import { getTerminals } from '../../service/AppSettingService';
import Grid from "rsuite/Grid";
import Row from "rsuite/Row";
import Col from "rsuite/Col";
import { Order, SPECIAL_REQUESTS } from "../../my-type";
import { authUserData } from "../../service";
import GooglePlacesAutocomplete from "../../components/GooglePlacesAutocomplete";
import Header from "../../layouts/Header";
import PageBase from "../../components/PageBase";
import SupplierSelect from "../../components/SupplierSelect";
import ChatAppSelect from "../../components/ChatAppSelect";
import { hasRole } from "../../util/roles";
import { getFlightSchedule, type FlightSchedule } from "../../service/TdxService";
import OrderLuggageSelector from "../../components/OrderLuggageSelector";
import { getUserById } from "../../service/UserService";
import { useDispatch } from "react-redux";
import { actionCreators } from "../../state";
import type { User } from "../../my-type";
import {
  buildLuggagesString,
  createEmptyLuggageSelection,
  type LuggageSelection,
} from "../../utilities/luggage";
import {
  BABY_SEAT_OPTIONS,
  buildSpecialRequestsWithBabySeats,
  hasInvalidBabySeatQuantity,
  type BabySeatSelection,
} from "../../utilities/babySeat";

const TAOYUAN_AIRPORT_GENERAL_TERMINAL = "桃園機場";
const FLIGHT_NO_PATTERN = /^[A-Z0-9]{1,2}\d{1,4}$/i;

const initialOrder = {
  id: 0,
  orderID: "x",
  userID: "",
  userUID: 0,
  orderType: OrderType.送機,
  address: "",
  destinationAddress: "",
  terminal: "",
  rideTime: new Date(),
  flightNo: "",
  flightTime: null,
  vehicleID: "",
  luggages: "",
  luggageNote: "",
  passengerName: "",
  passengerPhone: "",
  passengerID: "",
  passengerAdult: 1,
  passengerBaby: 0,
  rideFee: null,
  status: OrderStatus.派車中,
  teamCode: "",
  email: "",
  isBlindOrder: false,
  driverCommission: null,
  createdAt: new Date(),
  viaPoints: null,
  specialRequests: null,
  meetAndGreetDetails: null,
  supplierId: null,
  sourceChannel: null,
  externalBookingNo: null,
  chatApp: null,
  chatId: null,
  assignedCarModel: "",
};

export default function AdminCreateOrder() {
  const [order, setOrder] = useState<any>(initialOrder);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [vehicleOptions, setVehicleOptions] = useState<any[]>([]);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [terminalOptions, setTerminalOptions] = useState<any[]>([]);
  const [terminalError, setTerminalError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [destinationAddressError, setDestinationAddressError] = useState(false);
  const [rideTimeError, setRideTimeError] = useState(false);
  const [vehicleIDError, setVehicleIDError] = useState(false);
  const [passengerNameError, setPassengerNameError] = useState(false);
  const [passengerPhoneError, setPassengerPhoneError] = useState(false);
  const [passengerAdultError, setPassengerAdultError] = useState(false);
  const [rideFeeError, setRideFeeError] = useState(false);
  const [flightNoError, setFlightNoError] = useState(false);
  const [flightTimeError, setFlightTimeError] = useState(false);
  const [flightValidationWarning, setFlightValidationWarning] = useState("");
  const [driverCommissionPattern, setDriverCommissionPattern] = useState<string>("");
  const [commissionAmount, setCommissionAmount] = useState<number>(0);
  const [commissionReturnAmount, setCommissionReturnAmount] = useState<number>(0);
  const [viaPoints, setViaPoints] = useState<string[]>([""]);
  const [luggageSelection, setLuggageSelection] = useState<LuggageSelection>(createEmptyLuggageSelection());
  const [babySeatSelection, setBabySeatSelection] = useState<BabySeatSelection>({});
  const [specialRequests, setSpecialRequests] = useState<string[]>([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = authUserData();
  const [user, setUser] = useState<User | null>(authUser);
  const canManageOrders = hasRole(user?.roleString, "Driver");
  const showDriverBankWarning = !user?.driver?.bankID || !user?.driver?.bankAccount;
  const isAirportOrder =
    order.orderType === OrderType.送機 || order.orderType === OrderType.接機;
  const isPickupOrder = order.orderType === OrderType.接機;
  const requiresFlightDetails = isPickupOrder;
  const shouldValidateFlightSchedule =
    isAirportOrder && !!order.flightNo?.trim() && !!order.flightTime;

  useEffect(() => {
    let active = true;
    const authUserId = authUser?.id;
    const authToken = authUser?.jwtToken;
    const authRefreshToken = authUser?.refreshToken ?? "";
    const authLineAccessToken = authUser?.lineAccessToken ?? null;
    const authTokenExpiry = authUser?.tokenExpiry ?? null;

    const refreshCurrentUser = async () => {
      if (!authToken || !authUserId) {
        return;
      }

      const profileUser = await getUserById(authUserId);
      if (!active || !profileUser) {
        return;
      }

      const refreshedUser: User = {
        ...profileUser,
        jwtToken: authToken,
        refreshToken: profileUser.refreshToken ?? authRefreshToken,
        lineAccessToken: profileUser.lineAccessToken ?? authLineAccessToken,
        tokenExpiry: profileUser.tokenExpiry
          ? new Date(profileUser.tokenExpiry as string | number | Date)
          : authTokenExpiry,
      };

      setUser(refreshedUser);
      dispatch(actionCreators.updateUser(refreshedUser) as any);
    };

    refreshCurrentUser();

    return () => {
      active = false;
    };
  }, [
    authUser?.id,
    authUser?.jwtToken,
    authUser?.refreshToken,
    authUser?.lineAccessToken,
    authUser?.tokenExpiry,
    dispatch,
  ]);

  const formatFlightCheckDate = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getWeekdayKey = (date: Date) => {
    const weekdayKeys = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ] as const;

    return weekdayKeys[date.getDay()];
  };

  const shiftDateByDays = (date: Date, days: number) => {
    const shiftedDate = new Date(date);
    shiftedDate.setDate(shiftedDate.getDate() + days);
    return shiftedDate;
  };

  const parseScheduleTime = (time?: string | null) => {
    if (!time) {
      return { normalizedTime: "", dayOffset: 0 };
    }

    const match = time.match(/^(\d{2}:\d{2})(?:\+(\d+))?$/);
    if (!match) {
      return { normalizedTime: time, dayOffset: 0 };
    }

    return {
      normalizedTime: match[1],
      dayOffset: Number(match[2] ?? 0),
    };
  };

  const formatFlightTime = (date: Date) =>
    `${`${date.getHours()}`.padStart(2, "0")}:${`${date.getMinutes()}`.padStart(2, "0")}`;

  const getExpectedAirportId = (terminal: string | null | undefined) => {
    if (terminal === TAOYUAN_AIRPORT_GENERAL_TERMINAL) {
      return "TPE";
    }
    if (
      terminal === "桃園國際機場第一航廈" ||
      terminal === "桃園國際機場第二航廈"
    ) {
      return "TPE";
    }
    if (terminal === "台北松山機場" || terminal === "台北送山機場") {
      return "TSA";
    }
    if (terminal === "台中國際機場" || terminal === "台中國際機場(尚未提供服務)") {
      return "RMQ";
    }
    if (terminal === "高雄國際機場" || terminal === "高雄國際機場(尚未提供服務)") {
      return "KHH";
    }
    return null;
  };

  const getExpectedTerminal = (terminal: string | null | undefined) => {
    if (terminal === "桃園國際機場第一航廈") {
      return "1";
    }
    if (terminal === "桃園國際機場第二航廈") {
      return "2";
    }
    return null;
  };

  const validateFlightSchedule = async (): Promise<{
    isValid: boolean;
    requiresConfirmation: boolean;
    warningMessage: string;
  }> => {
    if (!shouldValidateFlightSchedule) {
      setFlightValidationWarning("");
      return { isValid: true, requiresConfirmation: false, warningMessage: "" };
    }

    const schedules = await getFlightSchedule(
      order.flightNo,
      formatFlightCheckDate(order.flightTime)
    );

    if (!schedules || schedules.length === 0) {
      const warningMessage = "此航班資訊目前無法透過 TDX 驗證，請確認後決定是否繼續送出。";
      setFlightValidationWarning(warningMessage);
      return { isValid: true, requiresConfirmation: true, warningMessage };
    }

    const weekdayKey = getWeekdayKey(order.flightTime);
    const selectedTime = formatFlightTime(order.flightTime);
    const expectedAirportId = getExpectedAirportId(order.terminal);
    const expectedTerminal = getExpectedTerminal(order.terminal);
    if (!expectedAirportId) {
      const warningMessage = "此航班資訊目前無法透過 TDX 驗證，請確認後決定是否繼續送出。";
      setFlightValidationWarning(warningMessage);
      return { isValid: true, requiresConfirmation: true, warningMessage };
    }

    const airportSchedules = schedules.filter((schedule: FlightSchedule) =>
      order.orderType === OrderType.接機
        ? schedule.ArrivalAirportID === expectedAirportId
        : schedule.DepartureAirportID === expectedAirportId
    );

    const isValidSchedule = airportSchedules.some((schedule: FlightSchedule) => {
      const { normalizedTime, dayOffset } =
        order.orderType === OrderType.接機
          ? parseScheduleTime(schedule.ArrivalTime)
          : parseScheduleTime(schedule.DepartureTime);
      const scheduleWeekdayKey =
        order.orderType === OrderType.接機 && dayOffset > 0
          ? getWeekdayKey(shiftDateByDays(order.flightTime, -dayOffset))
          : weekdayKey;
      const isWeekdayValid = schedule?.[scheduleWeekdayKey] === true;
      const isTimeValid = normalizedTime === selectedTime;
      const isTerminalValid = expectedTerminal ? schedule.Terminal === expectedTerminal : true;

      return isWeekdayValid && isTimeValid && isTerminalValid;
    });

    if (!isValidSchedule) {
      const warningMessage = "此航班資訊目前無法透過 TDX 驗證，請確認後決定是否繼續送出。";
      setFlightValidationWarning(warningMessage);
      return { isValid: true, requiresConfirmation: true, warningMessage };
    }

    setFlightValidationWarning("");
    return { isValid: true, requiresConfirmation: false, warningMessage: "" };
  };

  const updateViaPoint = (index: number, value: string) => {
    setViaPoints((prevViaPoints) =>
      prevViaPoints.map((point, pointIndex) => (pointIndex === index ? value : point))
    );
  };

  const addViaPoint = () => {
    setViaPoints((prevViaPoints) => [...prevViaPoints, ""]);
  };

  React.useEffect(() => {
    const loadVehicles = async () => {
      if (user?.team?.id) {
        setVehicleLoading(true);
        try {
          const vehicleList = await getAllTeamVehicles(user.team.id);
          if (vehicleList) {
            setVehicleOptions(
              vehicleList
                .filter((v: TeamVehicle) => v.enabled && v.isActive)
                .map((v: TeamVehicle) => ({
                  label: `${v.vehicleName} (${v.vehicleID})`,
                  value: v.vehicleID
                }))
            );
          }
        } catch (error) {
          console.error('Error fetching vehicles:', error);
        } finally {
          setVehicleLoading(false);
        }
      }
    };
    loadVehicles();

    // Get terminal options
    const terminals = getTerminals();
    setTerminalOptions(terminals.map((t: any) => ({ label: t.name, value: t.name })));
  }, [user?.team?.id]);

  // Unified change handler for rsuite Form
  const handleChange = (value: any) => {
    setOrder(value);
  };

  // Handle driver commission pattern change
  const handleCommissionPatternChange = (pattern: string) => {
    setDriverCommissionPattern(pattern);
    if (pattern === "") {
      setOrder((prevOrder: any) => ({ ...prevOrder, driverCommission: null }));
      setCommissionAmount(0);
      setCommissionReturnAmount(0);
    } else {
      // When pattern changes, keep existing amounts if any
      if (commissionAmount > 0 || commissionReturnAmount > 0) {
        updateCommission(pattern, commissionAmount, commissionReturnAmount);
      }
    }
  };

  // Format driver commission string
  const formatDriverCommission = (pattern: string, amount: number, returnAmount: number): string => {
    switch (pattern) {
      case "pattern1":
        return `收現:${amount},匯回:${returnAmount}`;
      case "pattern2":
        return `收現:${amount},匯回:0`;
      case "pattern3":
        return `收現:0,匯回:${amount}`;
      default:
        return "";
    }
  };

  // Update commission based on inputs
  const updateCommission = (pattern: string, amount: number, returnAmount: number) => {
    console.log("updateCommission called:", { pattern, amount, returnAmount });
    if (pattern) {
      if (amount > 0 || (pattern === "pattern1" && (amount > 0 || returnAmount > 0))) {
        const commissionStr = formatDriverCommission(pattern, amount, returnAmount);
        console.log("Setting commission to:", commissionStr);
        setOrder((prevOrder: any) => ({ ...prevOrder, driverCommission: commissionStr }));
      } else if (amount === 0 && returnAmount === 0) {
        console.log("Clearing commission (both amounts are 0)");
        setOrder((prevOrder: any) => ({ ...prevOrder, driverCommission: null }));
      }
    }
  };

  // Calculate estimated arrival time for 接機 orders
  const calculateEstArriveTimeForPickup = async (newOrder: Order): Promise<void> => {
    return new Promise((resolve, reject) => {
      const distanceMatrixService = new google.maps.DistanceMatrixService();

      // Build route segments: terminal -> via points -> address
      const routeSegments: string[] = [newOrder.terminal];

      // Add via points if they exist
      viaPoints
        .map((point) => point.trim())
        .filter((point) => point !== "")
        .forEach((point) => routeSegments.push(point));

      routeSegments.push(newOrder.address);

      // Calculate total duration by summing each segment
      let totalDurationSeconds = 0;

      const calculateSegmentDuration = (index: number) => {
        if (index >= routeSegments.length - 1) {
          // All segments calculated
          // Start with flight time, add 30 minutes for customs/luggage, then add route time
          const estArriveTime = new Date(newOrder.flightTime!.getTime());
          estArriveTime.setMinutes(estArriveTime.getMinutes() + 30); // Add 30 minutes for customs and luggage
          estArriveTime.setSeconds(estArriveTime.getSeconds() + totalDurationSeconds); // Add route time

          newOrder.estArriveTime = estArriveTime;
          resolve();
          return;
        }

        const request = {
          origins: [routeSegments[index]],
          destinations: [routeSegments[index + 1]],
          travelMode: google.maps.TravelMode.DRIVING
        };

        distanceMatrixService.getDistanceMatrix(request, function (result, status) {
          if (status === 'OK' && result!.rows[0].elements[0].status === 'OK') {
            totalDurationSeconds += result!.rows[0].elements[0].duration.value;
            calculateSegmentDuration(index + 1);
          } else {
            console.error(`Failed to calculate route segment ${index} to ${index + 1}`);
            reject(new Error('Failed to calculate route'));
          }
        });
      };

      // Start calculating from the first segment
      calculateSegmentDuration(0);
    });
  };

  const handleSubmit = async () => {
    //setOrder({ ...order, teamCode: user!.team.code });
    //setOrder({ ...order, orderID: "x" });
    //setOrder({ ...order, userID: user!.userId });
    //setOrder({ ...order, userUID: user!.id });

    // Validate required fields
    let hasError = false;

    if (!order.address || order.address.trim() === "") {
      setAddressError(true);
      hasError = true;
    } else {
      setAddressError(false);
    }

    // Validate terminal for 送機/接機 orders
    if (order.orderType === OrderType.送機 || order.orderType === OrderType.接機) {
      if (!order.terminal || order.terminal === "") {
        setTerminalError(true);
        hasError = true;
      } else {
        setTerminalError(false);
      }
    }

    // Validate destinationAddress for 接送 orders
    if (order.orderType === OrderType.接送) {
      if (!order.destinationAddress || order.destinationAddress.trim() === "") {
        setDestinationAddressError(true);
        hasError = true;
      } else {
        setDestinationAddressError(false);
      }
    }

    // Validate rideTime for 送機 and 接送 orders
    if (order.orderType === OrderType.送機 || order.orderType === OrderType.接送) {
      if (!order.rideTime) {
        setRideTimeError(true);
        hasError = true;
      } else {
        setRideTimeError(false);
      }
    }

    // Validate flightNo and flightTime for airport orders
    if (requiresFlightDetails) {
      if (!order.flightNo || order.flightNo.trim() === "" || !FLIGHT_NO_PATTERN.test(order.flightNo.trim())) {
        setFlightNoError(true);
        hasError = true;
      } else {
        setFlightNoError(false);
      }

      if (!order.flightTime) {
        setFlightTimeError(true);
        hasError = true;
      } else {
        setFlightTimeError(false);
      }
    }

    if (!order.vehicleID || order.vehicleID === "") {
      setVehicleIDError(true);
      hasError = true;
    } else {
      setVehicleIDError(false);
    }

    const formattedLuggages = buildLuggagesString(luggageSelection);

    if (!order.passengerName || order.passengerName.trim() === "") {
      setPassengerNameError(true);
      hasError = true;
    } else {
      setPassengerNameError(false);
    }

    if (!order.passengerPhone || order.passengerPhone.trim() === "") {
      setPassengerPhoneError(true);
      hasError = true;
    } else {
      setPassengerPhoneError(false);
    }

    if (!order.passengerAdult || order.passengerAdult < 1) {
      setPassengerAdultError(true);
      hasError = true;
    } else {
      setPassengerAdultError(false);
    }

    if (order.rideFee === null || order.rideFee === undefined || order.rideFee <= 0) {
      setRideFeeError(true);
      hasError = true;
    } else {
      setRideFeeError(false);
    }

    if (hasError) {
      alert("請填寫所有必填欄位");
      return;
    }

    if (hasInvalidBabySeatQuantity(babySeatSelection)) {
      setError("已勾選的嬰兒座椅數量需大於 0");
      return;
    }

    if (shouldValidateFlightSchedule) {
      setLoading(true);
      const flightValidationResult = await validateFlightSchedule();
      if (!flightValidationResult.isValid) {
        setLoading(false);
        return;
      }

      if (flightValidationResult.requiresConfirmation) {
        setLoading(false);
        const shouldContinue = window.confirm(flightValidationResult.warningMessage);
        if (!shouldContinue) {
          return;
        }
        setLoading(true);
      }
    }

    let newOrder: Order = { ...order, luggages: formattedLuggages };
    newOrder.teamCode = user!.team.code;
    newOrder.orderID = "x";
    newOrder.userID = user!.userId;
    newOrder.userUID = user!.id;
    newOrder.isAdminOrder = true;
    newOrder.isBlindOrder = false;
    newOrder.driverUID = 0;
    newOrder.createdAt = new Date(); // Set to current time at submission

    // Handle destinationAddress for 接送 orders
    newOrder.destinationAddress = order.destinationAddress && order.destinationAddress.trim() !== "" ? order.destinationAddress.trim() : null;

    // Set rideTime for 接機 orders (same as flightTime)
    if (order.orderType === OrderType.接機) {
      newOrder.rideTime = new Date(order.flightTime.getTime());
    }

    // Calculate estArriveTime
    if (order.orderType === OrderType.接機) {
      // For 接機 orders, calculate route time from terminal to address
      setLoading(true);
      try {
        await calculateEstArriveTimeForPickup(newOrder);
      } catch (err) {
        console.error("Failed to calculate route time:", err);
        // Fallback to default calculation
        newOrder.estArriveTime = new Date(newOrder.rideTime.getTime() + 30 * 60 * 1000);
      }
    } else {
      // For 送機 orders, default to rideTime + 30 minutes
      newOrder.estArriveTime = new Date(newOrder.rideTime.getTime() + 30 * 60 * 1000);
    }

    newOrder.driverCommission = order.driverCommission && order.driverCommission.trim() !== "" ? order.driverCommission : null;

    const assignedCarModel = order.assignedCarModel
      ? order.assignedCarModel.split(",").map((model: string) => model.trim()).filter(Boolean).join(", ")
      : "";
    newOrder.assignedCarModel = assignedCarModel !== "" ? assignedCarModel : null;

    const normalizedViaPoints = viaPoints.map((point) => point.trim()).filter((point) => point !== "");
    newOrder.viaPoints = normalizedViaPoints.length > 0 ? normalizedViaPoints : null;

    const combinedSpecialRequests = buildSpecialRequestsWithBabySeats(
      babySeatSelection,
      specialRequests
    );
    newOrder.specialRequests = combinedSpecialRequests.length > 0 ? combinedSpecialRequests : null;

    // Handle meet and greet details
    newOrder.meetAndGreetDetails = order.meetAndGreetDetails && order.meetAndGreetDetails.trim() !== "" ? order.meetAndGreetDetails.trim() : null;

    // Handle supplier field and clear retired external channel fields
    newOrder.supplierId = order.supplierId || null;
    newOrder.sourceChannel = null;
    newOrder.externalBookingNo = null;

    // Handle chat app fields
    newOrder.chatApp = order.chatApp || null;
    newOrder.chatId = order.chatId && order.chatId.trim() !== "" ? order.chatId.trim() : null;

    console.log(newOrder);

    setLoading(true);
    setError("");
    try {
      const result = await createOrder(newOrder);
      if (result) {
        alert("訂單建立成功");
        navigate("/orders");
      } else {
        setError("訂單建立失敗");
      }
    } catch (err) {
      setError("訂單建立失敗");
    } finally {
      setLoading(false);
    }
  };

  const header = <Header HeaderTitle="建立新訂單" isShowRefresh={false} OnReturn={() => navigate(-1)} OnRefresh={() => { }} />;

  if (!canManageOrders) {
    return (
      <AdminDashboard title="建立新訂單">
        <Message showIcon type="warning">
          沒有 Driver 角色的使用者不可建立訂單。
        </Message>
      </AdminDashboard>
    );
  }


  return (
    <AdminDashboard title="建立新訂單">
      <PageBase header={header} content={
        <div style={{ maxWidth: 900, marginLeft: 0 }}>
          {showDriverBankWarning && (
            <Message
              showIcon
              type="warning"
              style={{ marginBottom: 16 }}
            >
              請設定您的銀行帳號，佣金付款紀錄才會被建立，未設定銀行帳號不影響建單功能
            </Message>
          )}
          <Panel bordered>
            <Form
              fluid
              formValue={order}
              onChange={handleChange}
              onSubmit={handleSubmit}
            >
              <Grid fluid>
                <h2 className="fleet-form-section">01 · 行程與接送地點</h2>
                <Row gutter={16}>
                  <Col xs={8}>
                    <Form.Group controlId="orderType">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>訂單類型</Form.ControlLabel>
                      <SelectPicker
                        data={[{ label: "送機", value: OrderType.送機 }, { label: "接機", value: OrderType.接機 }, { label: "接送", value: OrderType.接送 }]}
                        name="orderType"
                        value={order.orderType}
                        onChange={value => setOrder({ ...order, orderType: value })}
                        style={{ width: "100%" }}
                      />
                    </Form.Group>
                  </Col>
                  {(order.orderType === OrderType.送機 || order.orderType === OrderType.接機) && (
                    <Col xs={8}>
                      <Form.Group controlId="terminal">
                        <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                          航廈 <span style={{ color: "red" }}>*</span>
                        </Form.ControlLabel>
                        <SelectPicker
                          data={terminalOptions}
                          name="terminal"
                          value={order.terminal}
                          onChange={value => {
                            setOrder({ ...order, terminal: value });
                            setTerminalError(false);
                            setFlightNoError(false);
                            setFlightTimeError(false);
                            setFlightValidationWarning("");
                          }}
                          style={{ width: "100%" }}
                          placeholder="請選擇航廈"
                          searchable
                          block
                        />
                        {terminalError && (
                          <Form.HelpText style={{ color: "red" }}>請選擇航廈</Form.HelpText>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                  {order.orderType === OrderType.接送 && (
                    <Col xs={16}>
                      <Form.Group controlId="destinationAddress">
                        <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                          目的地地址 <span style={{ color: "red" }}>*</span>
                        </Form.ControlLabel>
                        <GooglePlacesAutocomplete
                          value={order.destinationAddress}
                          onChange={value => {
                            setOrder({ ...order, destinationAddress: value });
                            setDestinationAddressError(false);
                          }}
                          placeholder="請輸入目的地地址"
                        />
                        {destinationAddressError && (
                          <Form.HelpText style={{ color: "red" }}>請輸入目的地地址</Form.HelpText>
                        )}
                      </Form.Group>
                    </Col>
                  )}
                </Row>
                <br />
                <Row gutter={16}>
                  {(order.orderType === OrderType.送機 || order.orderType === OrderType.接送) && (
                    <><Col xs={8}>
                      <Form.Group controlId="rideTime">
                        <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                          乘車時間 <span style={{ color: "red" }}>*</span>
                        </Form.ControlLabel>
                        <DatePicker
                          name="rideTime"
                          value={order.rideTime}
                          onChange={value => {
                            //setOrder({ ...order, rideTime: value })
                            let newDateStr = value!.toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' +
                              value!.toLocaleTimeString('zh-TW', { hourCycle: 'h24' });
                            console.log("datetime string:", newDateStr);
                            let newDateObj = new Date(newDateStr);
                            setOrder({ ...order, rideTime: newDateObj });
                            setRideTimeError(false);
                          }}
                          format="yyyy-MM-dd HH:mm"
                          style={{ width: "100%" }}
                          ranges={[]}
                          showMeridian={false}
                          hideMinutes={minute => minute % 5 !== 0}
                        />
                        {rideTimeError && (
                          <Form.HelpText style={{ color: "red" }}>請選擇乘車時間</Form.HelpText>
                        )}
                      </Form.Group>
                    </Col></>)
                  }
                  {(order.orderType === OrderType.送機 || order.orderType === OrderType.接機) && (
                    <><Col xs={8}>
                      <Form.Group controlId="flightNo">
                        <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                          航班號碼 {requiresFlightDetails && <span style={{ color: "red" }}>*</span>}
                        </Form.ControlLabel>
                        <Input
                          name="flightNo"
                          value={order.flightNo}
                          onChange={value => {
                            // Validate flight number format (e.g., BR123, 5J310)
                            if (value && !FLIGHT_NO_PATTERN.test(value.trim())) {
                              setFlightNoError(true);
                            } else {
                              setFlightNoError(false);
                            }
                            setFlightValidationWarning("");
                            setOrder({ ...order, flightNo: value });
                          }}
                        />
                        {flightNoError && (
                          <Form.HelpText style={{ color: "red" }}>
                            {requiresFlightDetails ? "請輸入正確的航班號碼格式（例如：BR123、5J310）" : "若有填寫，請輸入正確的航班號碼格式（例如：BR123、5J310）"}
                          </Form.HelpText>
                        )}
                      </Form.Group>
                    </Col><Col xs={8}>
                        <Form.Group controlId="flightTime">
                          <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                            航班時間 {requiresFlightDetails && <span style={{ color: "red" }}>*</span>}
                          </Form.ControlLabel>
                          <DatePicker
                            name="flightTime"
                            value={order.flightTime}
                            onChange={value => {
                              if (value) {
                                let newDateStr = value.toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' +
                                  value.toLocaleTimeString('zh-TW', { hourCycle: 'h24' });
                                console.log("datetime string:", newDateStr);
                                let newDateObj = new Date(newDateStr);
                                setOrder({ ...order, flightTime: newDateObj });
                              } else {
                                setOrder({ ...order, flightTime: null });
                              }
                              setFlightValidationWarning("");
                              setFlightTimeError(false);
                            }}
                            format="yyyy-MM-dd HH:mm"
                            style={{ width: "100%" }}
                            ranges={[]}
                            showMeridian={false}
                            hideMinutes={minute => minute % 5 !== 0}
                          />
                          {flightTimeError && (
                            <Form.HelpText style={{ color: "red" }}>
                              {requiresFlightDetails ? "請選擇航班時間" : "若有需要可選擇航班時間"}
                            </Form.HelpText>
                          )}
                        </Form.Group>
                      </Col></>)
                  }
                </Row>
                {flightValidationWarning && shouldValidateFlightSchedule && (
                  <>
                    <Row gutter={16}>
                      <Col xs={16}>
                        <Message showIcon type="warning">
                          {flightValidationWarning}
                        </Message>
                      </Col>
                    </Row>
                    <br />
                  </>
                )}
                <br />
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Group controlId="address">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                        {order.orderType === OrderType.接機 ? "下車地址" : "上車地址"} <span style={{ color: "red" }}>*</span>
                      </Form.ControlLabel>
                      <GooglePlacesAutocomplete
                        value={order.address}
                        onChange={value => {
                          setOrder({ ...order, address: value });
                          setAddressError(false);
                        }}
                        placeholder="請輸入地址，支援自動完成"
                      />
                      {addressError && (
                        <Form.HelpText style={{ color: "red" }}>請輸入地址</Form.HelpText>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                {viaPoints.map((viaPoint, index) => (
                  <React.Fragment key={`via-point-${index}`}>
                    <Row gutter={16}>
                      <Col xs={24}>
                        <Form.Group controlId={`viaPoint${index + 1}`}>
                          <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                            途經地點 {index + 1}
                          </Form.ControlLabel>
                          <GooglePlacesAutocomplete
                            value={viaPoint}
                            onChange={value => updateViaPoint(index, value)}
                            placeholder={`請輸入途經地點 ${index + 1}（選填）`}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <br />
                  </React.Fragment>
                ))}
                <Row gutter={16}>
                  <Col xs={24}>
                    <Button appearance="ghost" onClick={addViaPoint}>
                      新增途經地點
                    </Button>
                  </Col>
                </Row>
                <br />
                <h2 className="fleet-form-section">02 · 車型與行李</h2>
                <Row gutter={16}>
                  <Col xs={8}>
                    <Form.Group controlId="vehicleID">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                        車種 <span style={{ color: "red" }}>*</span>
                      </Form.ControlLabel>
                      <SelectPicker
                        data={vehicleOptions}
                        name="vehicleID"
                        value={order.vehicleID}
                        onChange={value => {
                          setOrder({ ...order, vehicleID: value });
                          setVehicleIDError(false);
                        }}
                        style={{ width: "100%" }}
                        placeholder={vehicleLoading ? "載入中..." : "請選擇車種"}
                        loading={vehicleLoading}
                        searchable
                        block
                      />
                      {vehicleIDError && (
                        <Form.HelpText style={{ color: "red" }}>請選擇車種</Form.HelpText>
                      )}
                    </Form.Group>
                  </Col>
                  <Col xs={8}>
                    <Form.Group controlId="assignedCarModel">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>指定車型</Form.ControlLabel>
                      <Input
                        name="assignedCarModel"
                        value={order.assignedCarModel}
                        onChange={value => setOrder({ ...order, assignedCarModel: value })}
                        placeholder="多個車型請用逗號分隔"
                      />
                      <Form.HelpText>例如：Camry, Altis</Form.HelpText>
                    </Form.Group>
                  </Col>
                  <Col xs={8}>
                    <Form.Group controlId="luggages">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>行李</Form.ControlLabel>
                      <OrderLuggageSelector
                        value={luggageSelection}
                        onChange={(nextValue, formattedValue) => {
                          setLuggageSelection(nextValue);
                          setOrder({ ...order, luggages: formattedValue });
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <h2 className="fleet-form-section">03 · 乘客與費用</h2>
                <Row gutter={16}>
                  <Col xs={8}>
                    <Form.Group controlId="email">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>Email</Form.ControlLabel>
                      <Input name="email" value={order.email} onChange={value => setOrder({ ...order, email: value })} />
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                <Row gutter={16}>
                  <Col xs={8}>
                    <Form.Group controlId="passengerName">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                        乘客姓名 <span style={{ color: "red" }}>*</span>
                      </Form.ControlLabel>
                      <Input
                        name="passengerName"
                        value={order.passengerName}
                        onChange={value => {
                          setOrder({ ...order, passengerName: value });
                          setPassengerNameError(false);
                        }}
                      />
                      {passengerNameError && (
                        <Form.HelpText style={{ color: "red" }}>請輸入乘客姓名</Form.HelpText>
                      )}
                    </Form.Group>
                  </Col>
                  <Col xs={8}>
                    <Form.Group controlId="passengerPhone">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                        乘客電話 <span style={{ color: "red" }}>*</span>
                      </Form.ControlLabel>
                      <Input
                        name="passengerPhone"
                        value={order.passengerPhone}
                        onChange={value => {
                          setOrder({ ...order, passengerPhone: value });
                          setPassengerPhoneError(false);
                        }}
                      />
                      {passengerPhoneError && (
                        <Form.HelpText style={{ color: "red" }}>請輸入乘客電話</Form.HelpText>
                      )}
                    </Form.Group>
                  </Col>
                  <Col xs={8}>
                    <Form.Group controlId="passengerID">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>乘客身分證</Form.ControlLabel>
                      <Input name="passengerID" value={order.passengerID} onChange={value => setOrder({ ...order, passengerID: value })} />
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                <Row gutter={16}>
                  <Col xs={8}>
                    <Form.Group controlId="passengerAdult">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                        成人人數 <span style={{ color: "red" }}>*</span>
                      </Form.ControlLabel>
                      <InputNumber
                        name="passengerAdult"
                        value={order.passengerAdult}
                        onChange={value => {
                          setOrder({ ...order, passengerAdult: value });
                          setPassengerAdultError(false);
                        }}
                        min={1}
                      />
                      {passengerAdultError && (
                        <Form.HelpText style={{ color: "red" }}>請輸入成人人數</Form.HelpText>
                      )}
                    </Form.Group>
                  </Col>
                  <Col xs={8}>
                    <Form.Group controlId="passengerBaby">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>嬰兒人數</Form.ControlLabel>
                      <InputNumber name="passengerBaby" value={order.passengerBaby} onChange={value => setOrder({ ...order, passengerBaby: value })} min={0} />
                    </Form.Group>
                  </Col>
                  <Col xs={8}>
                    <Form.Group controlId="rideFee">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                        乘車費用 <span style={{ color: "red" }}>*</span>
                      </Form.ControlLabel>
                      <InputNumber
                        name="rideFee"
                        value={order.rideFee}
                        onChange={value => {
                          setOrder({ ...order, rideFee: value });
                          setRideFeeError(false);
                        }}
                        min={1}
                      />
                      {rideFeeError && (
                        <Form.HelpText style={{ color: "red" }}>請輸入乘車費用</Form.HelpText>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Group controlId="specialRequests">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                        其他需求
                      </Form.ControlLabel>
                      <CheckPicker
                        data={SPECIAL_REQUESTS}
                        value={specialRequests}
                        onChange={value => setSpecialRequests(value)}
                        style={{ width: "100%" }}
                        placeholder="請選擇特殊需求（可多選）"
                        searchable={false}
                        block
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Group controlId="babySeatRequests">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                        嬰兒座椅
                      </Form.ControlLabel>
                      <div style={{ border: "1px solid #e5e5ea", borderRadius: 6, padding: 12 }}>
                        {BABY_SEAT_OPTIONS.map((label) => {
                          const checked = Object.prototype.hasOwnProperty.call(babySeatSelection, label);
                          const quantity = babySeatSelection[label] ?? null;

                          return (
                            <div
                              key={label}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 10,
                              }}
                            >
                              <Checkbox
                                checked={checked}
                                onChange={(_, nextChecked) => {
                                  setBabySeatSelection((prev) => {
                                    if (!nextChecked) {
                                      const nextSelection = { ...prev };
                                      delete nextSelection[label];
                                      return nextSelection;
                                    }

                                    return {
                                      ...prev,
                                      [label]: prev[label] && Number(prev[label]) > 0 ? prev[label] : 1,
                                    };
                                  });
                                }}
                              >
                                {label}
                              </Checkbox>
                              <InputNumber
                                min={1}
                                value={quantity}
                                disabled={!checked}
                                onChange={(value) =>
                                  setBabySeatSelection((prev) => ({
                                    ...prev,
                                    [label]: typeof value === "number" ? value : null,
                                  }))
                                }
                                style={{ width: 120 }}
                                placeholder="數量"
                              />
                            </div>
                          );
                        })}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                <h2 className="fleet-form-section">04 · 服務與聯絡資訊</h2>
                <Row gutter={16}>
                  <Col xs={8}>
                    <Form.Group controlId="supplierId">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>供應商</Form.ControlLabel>
                      <SupplierSelect
                        teamId={user?.team?.id || 0}
                        value={order.supplierId}
                        onChange={value => setOrder({ ...order, supplierId: value })}
                        placeholder="請選擇供應商"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                <Row gutter={16}>
                  <Col xs={8}>
                    <Form.Group controlId="chatApp">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>旅客聊天軟體</Form.ControlLabel>
                      <ChatAppSelect
                        value={order.chatApp}
                        onChange={value => setOrder({ ...order, chatApp: value })}
                        placeholder="請選擇聊天軟體"
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={16}>
                    <Form.Group controlId="chatId">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>旅客聊天 ID</Form.ControlLabel>
                      <Input
                        name="chatId"
                        value={order.chatId || ""}
                        onChange={value => setOrder({ ...order, chatId: value })}
                        placeholder="輸入旅客的聊天帳號或 ID"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Group controlId="meetAndGreetDetails">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>接機舉牌資訊</Form.ControlLabel>
                      <Input
                        as="textarea"
                        rows={2}
                        name="meetAndGreetDetails"
                        value={order.meetAndGreetDetails || ""}
                        onChange={value => setOrder({ ...order, meetAndGreetDetails: value })}
                        placeholder="例如：舉牌顯示姓名、公司名稱等"
                      />
                      <Form.HelpText>用於司機接機時舉牌顯示的資訊</Form.HelpText>
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Group controlId="luggageNote">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>乘車備註</Form.ControlLabel>
                      <Input
                        as="textarea"
                        rows={3}
                        name="luggageNote"
                        value={order.luggageNote || ""}
                        onChange={value => setOrder({ ...order, luggageNote: value })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <br />
                <h2 className="fleet-form-section">05 · 司機佣金</h2>
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Group controlId="driverCommissionPattern">
                      <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                        司機回饋模式
                      </Form.ControlLabel>
                      <SelectPicker
                        data={[
                          { label: "無", value: "" },
                          { label: "收現回饋 (收現{金額}回{金額})", value: "pattern1" },
                          { label: "收現免回 (收現{金額}免回)", value: "pattern2" },
                          { label: "免收客下匯 (免收客下匯{金額})", value: "pattern3" }
                        ]}
                        value={driverCommissionPattern}
                        onChange={value => handleCommissionPatternChange(value || "")}
                        style={{ width: "100%" }}
                        placeholder="請選擇司機回饋模式"
                        searchable={false}
                        cleanable
                        block
                      />
                    </Form.Group>
                  </Col>
                </Row>
                {driverCommissionPattern && (
                  <Row gutter={16}>
                    {(driverCommissionPattern === "pattern1" || driverCommissionPattern === "pattern2" || driverCommissionPattern === "pattern3") && (
                      <Col xs={driverCommissionPattern === "pattern1" ? 12 : 24}>
                        <Form.Group controlId="commissionAmount">
                          <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                            {driverCommissionPattern === "pattern3" ? "匯款金額" : "收現金額"}
                          </Form.ControlLabel>
                          <InputNumber
                            value={commissionAmount}
                            onChange={value => {
                              console.log("commission amount changed - raw value:", value, "type:", typeof value);
                              const numValue = (value === "" || value === null || value === undefined) ? 0 : Number(value);
                              console.log("commission amount - parsed numValue:", numValue);
                              setCommissionAmount(numValue);
                              updateCommission(driverCommissionPattern, numValue, commissionReturnAmount);
                            }}
                            min={0}
                            style={{ width: "100%" }}
                          />
                        </Form.Group>
                      </Col>
                    )}
                    {driverCommissionPattern === "pattern1" && (
                      <Col xs={12}>
                        <Form.Group controlId="commissionReturnAmount">
                          <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>
                            回饋金額
                          </Form.ControlLabel>
                          <InputNumber
                            value={commissionReturnAmount}
                            onChange={value => {
                              console.log("commission return amount changed - raw value:", value, "type:", typeof value);
                              const numValue = (value === "" || value === null || value === undefined) ? 0 : Number(value);
                              console.log("commission return amount - parsed numValue:", numValue);
                              setCommissionReturnAmount(numValue);
                              updateCommission(driverCommissionPattern, commissionAmount, numValue);
                            }}
                            min={0}
                            style={{ width: "100%" }}
                          />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                )}
                {order.driverCommission && (
                  <Row gutter={16}>
                    <Col xs={24}>
                      <Message showIcon type="info" style={{ marginBottom: 16 }}>
                        <strong>司機回饋:</strong> {order.driverCommission}
                      </Message>
                    </Col>
                  </Row>
                )}
                <Row gutter={16}>
                  {/* <Col xs={12}>
                  <Form.Group controlId="email">
                    <Form.ControlLabel style={{ textAlign: "left", display: "block" }}>Email</Form.ControlLabel>
                    <Input name="email" value={order.email} onChange={value => setOrder({ ...order, email: value })} />
                  </Form.Group>
                </Col> */}
                </Row>
              </Grid>
              {error && <Message showIcon type="error">{error}</Message>}
              <br></br>
              <Form.Group>
                <Button appearance="primary" type="submit" loading={loading} block>
                  {loading ? <Loader /> : "建立訂單"}
                </Button>
              </Form.Group>
            </Form>
          </Panel>
        </div>}
        showSide={false} />
    </AdminDashboard>
  );
}
